/*
 * Button: the one control for actions. Variants: primary (one per view, brand-600), secondary (white with a
 * hairline), ghost (quiet), danger (destructive), link (inline text). Sizes sm / md / lg; `loading` shows a
 * spinner, keeps the width and disables the button. `asChild` renders the styles on a child (e.g. a Link).
 *
 *   <Button onClick={save} loading={m.isPending}>{t('actions.save')}</Button>
 *   <Button variant="secondary" asChild><Link to="/servers/new">…</Link></Button>
 */
import { Slot } from 'radix-ui'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-600 text-on-brand shadow-button-primary hover:bg-brand-700 active:bg-brand-800 disabled:bg-brand-300 disabled:shadow-none',
  secondary:
    'bg-surface text-ink shadow-sm ring-1 ring-hairline-strong ring-inset hover:bg-surface-soft hover:ring-line active:bg-surface-quiet disabled:text-subtle',
  ghost: 'text-body hover:bg-control hover:text-ink active:bg-control-strong disabled:text-subtle',
  danger:
    'bg-danger-600 text-white shadow-[0_6px_14px_-8px_var(--color-danger-600)] hover:bg-danger-700 disabled:bg-danger-100 disabled:text-danger-700 disabled:shadow-none',
  link: 'h-auto px-0 text-brand-700 underline-offset-4 hover:underline disabled:text-subtle',
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 rounded-[10px] px-3 text-13 touch:min-h-11',
  md: 'h-10 gap-2 rounded-xl px-4 text-14 touch:min-h-11',
  lg: 'h-12 gap-2 rounded-xl px-5 text-15',
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** icon before the label (start side) */
  icon?: ReactNode
  /** icon after the label (end side), e.g. a forward arrow with `flip-ltr` */
  iconEnd?: ReactNode
  asChild?: boolean
  block?: boolean
}

export const buttonClasses = ({
  variant = 'primary',
  size = 'md',
  block,
  className,
}: { variant?: ButtonVariant; size?: ButtonSize; block?: boolean; className?: string } = {}) =>
  cn(
    'relative inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap transition-[background-color,box-shadow,color] duration-150 select-none disabled:cursor-not-allowed [&_svg]:shrink-0',
    SIZE[size],
    VARIANT[variant],
    variant === 'link' && 'h-auto rounded-md px-0',
    block && 'w-full',
    className,
  )

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    icon,
    iconEnd,
    asChild,
    block,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common')
  const classes = buttonClasses({ variant, size, block, className })
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} {...rest}>
        {icon}
        <Slot.Slottable>{children}</Slot.Slottable>
        {iconEnd}
      </Slot.Root>
    )
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner />
          <span className="sr-only">{t('ui.loading')}</span>
        </span>
      ) : null}
      <span className={cn('inline-flex items-center gap-[inherit]', loading && 'invisible')}>
        {icon}
        {children}
        {iconEnd}
      </span>
    </button>
  )
})
